import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Banknote, Copy, Loader2, QrCode } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';

// Cast — tab_precos_avulsos_unidade ainda não está em database.types.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

interface Maquina {
  seq_maquina: number;
  nom_maquina: string;
  seq_unidade: number;
  tab_unidades: { nom_unidade: string };
}

interface TipoLavagem {
  seq_tipo_lavagem: number;
  cod_tipo_lavagem: string;
  nom_tipo_lavagem: string;
}

interface RespostaPix {
  seq_pagamento_avulso: number;
  seq_pagamento: number;
  val_pagamento: number;
  cod_status_asaas: string;
  des_link_fatura: string;
  pix_qrcode_base64: string | null;
  pix_copia_cola: string | null;
}

async function extrairMensagemErro(error: unknown): Promise<string> {
  if (!error) return 'Erro desconhecido';
  const e = error as {
    message?: string;
    context?: { body?: ReadableStream | Response };
  };
  try {
    const ctx = e.context;
    if (ctx?.body) {
      const txt =
        ctx.body instanceof Response
          ? await ctx.body.text()
          : await new Response(ctx.body as ReadableStream).text();
      try {
        const json = JSON.parse(txt) as { erro?: string };
        return json.erro ?? txt;
      } catch {
        return txt;
      }
    }
  } catch {
    /* ignore */
  }
  return e.message ?? String(error);
}

/**
 * /avulso/comprar — cliente compra uma lavagem AVULSA (sem assinatura).
 * Fluxo:
 *  1) Seleciona maquina (mostra unidade) + tipo de lavagem
 *  2) Sistema busca preço em tab_precos_avulsos_unidade (fallback do edge fn)
 *  3) Cliente clica "Pagar via PIX"
 *  4) Edge fn `iniciar-pagamento-avulso` cria pagamento Asaas + retorna QR Code
 *  5) Ao confirmar pagamento (webhook Asaas), webhook chama `gerar-código-validação`
 *     que já envia o código via WhatsApp (modalidade=AVULSO).
 */
export function ComprarAvulsoPage() {
  const [seqMaquina, setSeqMaquina] = useState('');
  const [seqTipoLavagem, setSeqTipoLavagem] = useState('');
  const [numPlaca, setNumPlaca] = useState('');
  const [resposta, setResposta] = useState<RespostaPix | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const maquinas = useQuery({
    queryKey: ['avulso-maquinas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_maquinas')
        .select('seq_maquina, nom_maquina, seq_unidade, tab_unidades(nom_unidade)')
        .eq('ind_ativo', true);
      if (error) throw error;
      return (data ?? []) as unknown as Maquina[];
    },
  });

  const tipos = useQuery({
    queryKey: ['avulso-tipos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_tipos_lavagem')
        .select('seq_tipo_lavagem, cod_tipo_lavagem, nom_tipo_lavagem')
        .eq('ind_ativo', true)
        .order('seq_tipo_lavagem');
      if (error) throw error;
      return (data ?? []) as TipoLavagem[];
    },
  });

  // Resolve unidade da maquina selecionada
  const maquinaSelecionada = maquinas.data?.find((m) => String(m.seq_maquina) === seqMaquina);
  const seqUnidade = maquinaSelecionada?.seq_unidade;

  // Busca preço vigente para (unidade, tipo)
  const preco = useQuery({
    queryKey: ['avulso-preco', seqUnidade, seqTipoLavagem],
    queryFn: async () => {
      if (!seqUnidade || !seqTipoLavagem) return null;
      const { data } = await sb
        .from('tab_precos_avulsos_unidade')
        .select('val_lavagem_avulsa')
        .eq('seq_unidade', seqUnidade)
        .eq('seq_tipo_lavagem', Number(seqTipoLavagem))
        .eq('ind_ativo', true)
        .maybeSingle();
      return data ? Number(data.val_lavagem_avulsa) : null;
    },
    enabled: !!(seqUnidade && seqTipoLavagem),
  });

  const pagar = useMutation({
    mutationFn: async () => {
      setErro(null);
      const { data, error } = await supabase.functions.invoke('iniciar-pagamento-avulso', {
        body: {
          seq_maquina: Number(seqMaquina),
          seq_tipo_lavagem: Number(seqTipoLavagem),
          num_placa: numPlaca.trim() || undefined,
        },
      });
      if (error) {
        const msg = await extrairMensagemErro(error);
        throw new Error(msg);
      }
      if (data && typeof data === 'object' && 'erro' in data) {
        throw new Error(String((data as { erro: string }).erro));
      }
      return data as RespostaPix;
    },
    onSuccess: (r) => {
      setResposta(r);
      toast.success(`Pagamento PIX gerado — R$ ${Number(r.val_pagamento).toFixed(2)}`, {
        description:
          'Escaneie o QR Code ou copie o código. Você recebera o código via WhatsApp após confirmar.',
      });
    },
    onError: (e: Error) => {
      setErro(e.message);
      toast.error('Falha ao iniciar pagamento', { description: traduzirErroSupabase(e) });
    },
  });

  function reset() {
    setResposta(null);
    setErro(null);
  }

  function copiarPix() {
    if (!resposta?.pix_copia_cola) return;
    void navigator.clipboard.writeText(resposta.pix_copia_cola);
    toast.success('PIX copia-e-cola copiado');
  }

  return (
    <div className="space-y-4 p-4 pb-20">
      <div className="flex items-center gap-3">
        <div className="bg-octa-gradient shadow-octa-glow flex h-10 w-10 items-center justify-center rounded-xl">
          <Banknote className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Comprar lavagem avulsa</h1>
          <p className="text-muted-foreground text-xs">
            Sem assinatura — pague via PIX e receba o codigo.
          </p>
        </div>
      </div>

      {resposta ? (
        <Card variant="elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <QrCode className="h-5 w-5" />
              Pague R$ {Number(resposta.val_pagamento).toFixed(2)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {resposta.pix_qrcode_base64 ? (
              <div className="flex justify-center">
                <img
                  src={`data:image/png;base64,${resposta.pix_qrcode_base64}`}
                  alt="QR Code PIX"
                  className="h-56 w-56 rounded-lg border bg-white p-2"
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                QR Code indisponivel — use o link da fatura abaixo.
              </p>
            )}

            {resposta.pix_copia_cola && (
              <div className="space-y-2">
                <Label>PIX copia-e-cola</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={resposta.pix_copia_cola}
                    className="text-xs tracking-wider"
                    aria-label="Código PIX copia-e-cola"
                  />
                  <Button variant="outline" size="icon" onClick={copiarPix} aria-label="Copiar PIX">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-muted/40 rounded-lg p-3 text-xs">
              <p className="font-medium">Após pagar:</p>
              <ul className="text-muted-foreground mt-1 list-disc space-y-0.5 pl-4">
                <li>Você recebera o código de validação por WhatsApp</li>
                <li>Tambem ficara disponivel no app em &quot;Notificacoes&quot;</li>
                <li>Digite o código no totem da unidade pra liberar a lavagem</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} className="flex-1">
                Voltar
              </Button>
              {resposta.des_link_fatura && (
                <Button variant="default" asChild className="flex-1">
                  <a href={resposta.des_link_fatura} target="_blank" rel="noopener noreferrer">
                    Abrir fatura
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="space-y-4 p-4">
            {erro && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">Não foi possivel iniciar o pagamento</p>
                  <p className="text-xs">{erro}</p>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Unidade / Maquina</Label>
              {maquinas.isLoading ? (
                <Skeleton className="h-10" />
              ) : maquinas.data && maquinas.data.length > 0 ? (
                <Select value={seqMaquina} onValueChange={setSeqMaquina}>
                  <SelectTrigger>
                    <SelectValue placeholder="Onde você está?" />
                  </SelectTrigger>
                  <SelectContent>
                    {maquinas.data.map((m) => (
                      <SelectItem key={m.seq_maquina} value={String(m.seq_maquina)}>
                        {m.tab_unidades?.nom_unidade} — {m.nom_maquina}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-muted-foreground text-xs">Nenhuma maquina ativa no momento.</p>
              )}
            </div>

            <div className="space-y-1">
              <Label>Tipo de lavagem</Label>
              {tipos.isLoading ? (
                <Skeleton className="h-10" />
              ) : tipos.data && tipos.data.length > 0 ? (
                <Select value={seqTipoLavagem} onValueChange={setSeqTipoLavagem}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tipos.data.map((t) => (
                      <SelectItem key={t.seq_tipo_lavagem} value={String(t.seq_tipo_lavagem)}>
                        {t.nom_tipo_lavagem}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-muted-foreground text-xs">
                  Nenhum tipo de lavagem cadastrado ainda.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label>Placa (opcional)</Label>
              <Input
                placeholder="ABC1D23"
                value={numPlaca}
                onChange={(e) => setNumPlaca(e.target.value.toUpperCase().slice(0, 8))}
                maxLength={8}
              />
              <p className="text-muted-foreground text-xs">
                Ajuda a identificar a lavagem no historico, mas nao eh obrigatorio.
              </p>
            </div>

            {seqMaquina && seqTipoLavagem && (
              <div className="bg-octa-gradient rounded-lg p-4 text-white">
                <p className="text-xs uppercase tracking-wider text-white/80">Valor da lavagem</p>
                {preco.isLoading ? (
                  <Skeleton className="mt-1 h-8 w-32 bg-white/20" />
                ) : preco.data != null ? (
                  <p className="text-3xl font-bold">R$ {preco.data.toFixed(2)}</p>
                ) : (
                  <>
                    <p className="text-3xl font-bold">a calcular</p>
                    <p className="text-xs text-white/70">
                      Sem preco cadastrado nesta combinacao — o sistema usara o fallback do plano.
                    </p>
                  </>
                )}
              </div>
            )}

            <Button
              onClick={() => pagar.mutate()}
              disabled={pagar.isPending || !seqMaquina || !seqTipoLavagem}
              className="w-full"
            >
              {pagar.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando PIX...
                </>
              ) : (
                <>
                  <QrCode className="mr-2 h-4 w-4" />
                  Pagar via PIX
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
